import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Amplify } from 'aws-amplify';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  getCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { extractRole, type AuthUser, type UserRole } from '@/lib/auth';

// ---------------------------------------------------------------------------
// Configure Amplify from environment variables
// ---------------------------------------------------------------------------
const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as string;
const userPoolClientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string;

if (userPoolId && userPoolClientId) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
        loginWith: {
          email: true,
        },
      },
    },
  });
}

// ---------------------------------------------------------------------------
// Context types
// ---------------------------------------------------------------------------
export interface AuthContextValue {
  user: AuthUser | null;
  role: UserRole;
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;
  idToken: string | null;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
export { AuthContext };

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Restore session from Amplify on mount
  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      try {
        const [cognitoUser, session] = await Promise.all([
          getCurrentUser(),
          fetchAuthSession(),
        ]);

        if (cancelled) return;

        const sessionIdToken = session.tokens?.idToken;
        const token = session.tokens?.accessToken?.toString() ?? null;
        const role = sessionIdToken ? extractRole(sessionIdToken.payload) : 'read-only';

        setUser({
          userId: cognitoUser.userId,
          username: cognitoUser.username,
          email: (sessionIdToken?.payload?.email as string | undefined) ?? '',
          role,
        });
        setAccessToken(token);
        setIdToken(sessionIdToken?.toString() ?? null);
      } catch {
        // No active session — user is unauthenticated
        if (!cancelled) {
          setUser(null);
          setAccessToken(null);
          setIdToken(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  // Listen for token refresh events from Amplify Hub
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'tokenRefresh') {
        // Silently update the stored tokens
        fetchAuthSession()
          .then((session) => {
            setAccessToken(session.tokens?.accessToken?.toString() ?? null);
            setIdToken(session.tokens?.idToken?.toString() ?? null);
          })
          .catch(() => {
            // Refresh failed — handled by tokenRefresh_failure below
          });
      }

      if (payload.event === 'tokenRefresh_failure') {
        setUser(null);
        setAccessToken(null);
        setIdToken(null);
        setError('session-expired');
      }
    });

    return unsubscribe;
  }, []);

  const signIn = useCallback(async (username: string, password: string) => {
    setError(null);
    try {
      await amplifySignIn({ username, password });

      const [cognitoUser, session] = await Promise.all([
        getCurrentUser(),
        fetchAuthSession(),
      ]);

      const sessionIdToken = session.tokens?.idToken;
      const token = session.tokens?.accessToken?.toString() ?? null;
      const role = sessionIdToken ? extractRole(sessionIdToken.payload) : 'read-only';

      setUser({
        userId: cognitoUser.userId,
        username: cognitoUser.username,
        email: (sessionIdToken?.payload?.email as string | undefined) ?? '',
        role,
      });
      setAccessToken(token);
      setIdToken(sessionIdToken?.toString() ?? null);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Authentication failed.';
      setError(message);
      throw err;
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await amplifySignOut();
    } finally {
      setUser(null);
      setAccessToken(null);
      setIdToken(null);
      setError(null);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      role: user?.role ?? 'read-only',
      isAuthenticated: user !== null,
      isLoading,
      accessToken,
      idToken,
      signIn,
      signOut,
      error,
    }),
    [user, isLoading, accessToken, idToken, signIn, signOut, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
